import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { useFavorites } from '../context/FavoritesContext';
import { ShoppingCart, ChevronLeft, ChevronRight, Tag, Heart } from 'lucide-react';
import { Button } from './ui/button';
import OfferModal from './OfferModal';
import ShareButtons from './ShareButtons';
import AskOnWhatsApp from './AskOnWhatsApp';
import { formatDimensions, formatUSD } from '../utils/format';
import { slugify } from '../utils/slug';
import { trackArtworkView } from '../utils/track';
import './Sculptures.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Sculptures = () => {
  const [selectedSculpture, setSelectedSculpture] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [sculptures, setSculptures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const { addToCart } = useCart();
  const { t, lang } = useLanguage();
  const { isFavorite, toggleFavorite } = useFavorites();

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/artworks?category=sculpture`, { cache: 'no-store' })
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        // Reorder so that favorited pieces appear first
        const favs = JSON.parse(localStorage.getItem('favorites') || '[]');
        const sorted = [...list].sort((a, b) => {
          const aFav = favs.includes(a.id) ? 0 : 1;
          const bFav = favs.includes(b.id) ? 0 : 1;
          return aFav - bFav;
        });
        setSculptures(sorted);
      })
      .catch(() => setSculptures([]))
      .finally(() => setLoading(false));
  }, []);

  const handleAddToCart = (sculpture) => {
    if (sculpture.available) {
      addToCart(sculpture);
      setSelectedSculpture(null);
    }
  };

  const openModal = (sculpture) => {
    setSelectedSculpture(sculpture);
    setCurrentImageIndex(0);
    trackArtworkView(sculpture);
    const slug = sculpture.slug || slugify(sculpture.title);
    if (slug && typeof window !== 'undefined') {
      window.history.pushState({ sculptureModal: true }, '', `/white-dolls/${slug}`);
    }
  };

  const closeModal = () => {
    setSelectedSculpture(null);
    setCurrentImageIndex(0);
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/white-dolls/')) {
      window.history.back();
    }
  };

  useEffect(() => {
    const onPop = () => {
      if (!window.location.pathname.startsWith('/white-dolls/')) {
        setSelectedSculpture(null);
        setCurrentImageIndex(0);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const getImagesArray = (sculpture) => {
    return [
      { url: sculpture.image, label: t('label_full_sculpture') },
      { url: sculpture.scene, label: t('label_in_scene') }
    ];
  };

  const nextImage = () => {
    if (selectedSculpture) {
      const images = getImagesArray(selectedSculpture);
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    if (selectedSculpture) {
      const images = getImagesArray(selectedSculpture);
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  return (
    <section id="sculptures" className="sculptures-section">
      <div className="sculptures-container">
        <div className="section-header">
          <h2 className="section-title">{t('sculptures_title')}</h2>
          <p className="section-description">
            {t('sculptures_description')}
          </p>
        </div>

        <div className="sculptures-grid">
          {loading && sculptures.length === 0 && (
            <p className="works-loading">{lang === 'es' ? 'Cargando…' : 'Loading…'}</p>
          )}
          {!loading && sculptures.length === 0 && (
            <p className="works-empty">{lang === 'es' ? 'Próximamente nuevas piezas.' : 'New pieces coming soon.'}</p>
          )}
          {sculptures.map((sculpture, index) => (
            <div 
              key={sculpture.id} 
              className="sculpture-item"
              style={{ animationDelay: `${index * 0.15}s` }}
              onClick={() => openModal(sculpture)}
              data-testid={`sculpture-item-${sculpture.id}`}
            >
              <div className="sculpture-image-wrapper">
                <img 
                  src={sculpture.image} 
                  alt={sculpture.title}
                  className="sculpture-image"
                />
                <div className={`work-status-badge ${sculpture.available ? 'available' : 'sold'}`}>
                  {sculpture.available ? t('badge_available') : t('badge_sold')}
                </div>
                <button 
                  className={`favorite-btn ${isFavorite(sculpture.id) ? 'active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); toggleFavorite(sculpture.id); }}
                  aria-label="Save to favorites"
                >
                  <Heart size={18} fill={isFavorite(sculpture.id) ? '#000' : 'none'} />
                </button>
                <div className="sculpture-overlay">
                  <span className="sculpture-view-text">{t('view_details')}</span>
                </div>
              </div>
              <div className="sculpture-info">
                <h3 className="sculpture-title" translate="no">{sculpture.title}</h3>
                {sculpture.price > 0 && (
                  <p className="sculpture-price" translate="no">{formatUSD(sculpture.price)} USD</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedSculpture && (
        <div className="work-modal" onClick={closeModal}>
          <div className="work-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal} data-testid="sculpture-modal-close">
              ×
            </button>
            <div className="modal-grid">
              <div className="modal-gallery">
                <div className="modal-image-container">
                  <img 
                    src={getImagesArray(selectedSculpture)[currentImageIndex].url} 
                    alt={selectedSculpture.title} 
                  />
                  <button className="gallery-nav prev" onClick={prevImage}>
                    <ChevronLeft size={24} />
                  </button>
                  <button className="gallery-nav next" onClick={nextImage}>
                    <ChevronRight size={24} />
                  </button>
                </div>
                <div className="gallery-thumbnails gallery-thumbnails-2">
                  {getImagesArray(selectedSculpture).map((img, idx) => (
                    <div 
                      key={idx}
                      className={`thumbnail ${currentImageIndex === idx ? 'active' : ''}`}
                      onClick={() => setCurrentImageIndex(idx)}
                    >
                      <img src={img.url} alt={img.label} />
                      <span className="thumbnail-label">{img.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-details">
                <div className={`modal-status ${selectedSculpture.available ? 'available' : 'sold'}`}>
                  {selectedSculpture.available ? t('badge_available') : t('badge_sold')}
                </div>
                <h3 translate="no">{selectedSculpture.title}</h3>
                <p className="modal-year">{selectedSculpture.year}</p>
                <p className="modal-medium">{selectedSculpture.medium}</p>
                <p className="modal-dimensions" translate="no">{formatDimensions(selectedSculpture.dimensions, lang)}</p>
                {selectedSculpture.available && (
                  <>
                    <p className="modal-price" translate="no">{formatUSD(selectedSculpture.price)} USD</p>
                    <ul className="modal-details-list">
                      <li className="detail-shipping">{t('detail_shipping')}</li>
                      <li>{t('detail_signed')}</li>
                      <li>{t('detail_certificate')}</li>
                    </ul>
                    <Button 
                      onClick={() => handleAddToCart(selectedSculpture)}
                      className="add-to-cart-button"
                    >
                      <ShoppingCart size={20} />
                      {t('add_to_cart')}
                    </Button>
                    <AskOnWhatsApp title={selectedSculpture.title} testId="ask-whatsapp-sculpture-btn" />
                    {selectedSculpture.price > 2500 && (
                      <button 
                        onClick={() => setShowOfferModal(true)}
                        className="make-offer-button"
                      >
                        <Tag size={18} />
                        {t('make_offer')}
                      </button>
                    )}
                  </>
                )}
                <ShareButtons artwork={selectedSculpture} />
              </div>
            </div>

            {(() => {
              const price = selectedSculpture.price || 0;
              const range = Math.max(price * 0.4, 500);
              const similar = sculptures
                .filter(a => a.id !== selectedSculpture.id && a.available && Math.abs((a.price || 0) - price) <= range)
                .slice(0, 3);
              if (similar.length === 0) return null;
              return (
                <div className="you-may-like-section">
                  <h4 className="you-may-like-title">{t('you_may_like')}</h4>
                  <div className="you-may-like-grid">
                    {similar.map(item => (
                      <div 
                        key={item.id} 
                        className="suggestion-item"
                        onClick={() => {
                          setSelectedSculpture(item);
                          setCurrentImageIndex(0);
                          const slug = item.slug || slugify(item.title);
                          if (slug) window.history.replaceState({ sculptureModal: true }, '', `/white-dolls/${slug}`);
                        }}
                      >
                        <div className="suggestion-image">
                          <img src={item.image} alt={item.title} />
                        </div>
                        <div className="suggestion-info">
                          <p className="suggestion-title" translate="no">{item.title}</p>
                          <p className="suggestion-price" translate="no">{formatUSD(item.price)} USD</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {showOfferModal && selectedSculpture && (
        <OfferModal 
          artwork={selectedSculpture}
          onClose={() => setShowOfferModal(false)}
        />
      )}
    </section>
  );
};

export default Sculptures;
